///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2023
/// Copyright (c) 2023 Geodata Labs
///

import { cube } from "@stovokor/math/cube"
import { type vec3 } from "@stovokor/math/vector"
import { type VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { StovokorContext } from "@stovokor/StovokorContext"

export class AttributeBinding {
	constructor(public info: WebGLActiveInfo, public location: number) {}

	public bindingFunction(value: VertexAttribute): () => void {
		return () => {
			value.bind(this.location)
		}
	}
}

export class VertexAttribute {
	public readonly size: number
	public offset: number = 0
	public glType: number = 0x1406 // WebGL2RenderingContext.FLOAT
	public typeSize: number = 4
	public values: number[]
	public bounds = new cube()
	public buffer?: VertexBuffer
	/**
	 * Instance divisor: 0 advances per vertex (the default), 1 per instance
	 * for attributes read from an instance buffer by an instanced draw
	 * (Geometry.instanceCount)
	 */
	public divisor: number = 0
	private context: StovokorContext

	constructor(context: StovokorContext, size: number, values: number[] = []) {
		this.context = context
		this.size = size
		this.values = values
		this.calculateBounds()
	}

	public get empty(): boolean {
		return this.values.length == 0
	}

	public get count(): number {
		return this.values.length / this.size
	}

	public add(...values: number[]): void {
		this.values.push(...values)
	}

	public addPos(...values: number[]): void {
		this.values.push(...values)
		this.bounds.update(values[0], values[1], values[2])
	}

	public addVec(v: vec3): void {
		this.values.push(v.x, v.y, v.z)
		this.bounds.update(v.x, v.y, v.z)
	}

	public clearVertices(): void {
		this.values = []
	}

	// TODO: Get rid of this. It's a hack to calculate the bounds of the attribute
	public calculateBounds(): void {
		if(this.size == 3 && this.values.length % 3 == 0) {
			for(let i=0;i<this.values.length;i+=3) {
				this.bounds.update(this.values[i], this.values[i+1], this.values[i+2])
			}
		}
	}

	public bind(binding: number): void {
		if(this.buffer !== undefined) {
			this.buffer.bind()  // re-binding this is probably not harmful
			this.context.gl.enableVertexAttribArray(binding)
			this.context.gl.vertexAttribPointer(binding, this.size, this.glType, false, this.buffer.stride * this.typeSize, this.offset * this.typeSize)
			// Divisor is vertex-array state like the pointer; a fresh array
			// starts at 0, so only non-default values need setting
			if(this.divisor > 0) {
				this.context.gl.vertexAttribDivisor(binding, this.divisor)
			}
		}
	}

	public unbind(binding: number): void {
		this.context.gl.disableVertexAttribArray(binding)
	}
}